import { useEffect, useState } from "react";

import { TimePicker12 } from "@/components/reusables/time-picker/time-picker-12hour";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { WeekDays } from "@/constants/constants";
import { TimeShift } from "@/interfaces/timeTracking";
import {
  dateToSec,
  secToDate,
} from "@/utils/helper-functions/date-time.helper";

type WorkingHourProps = {
  shift: TimeShift;
  shiftData: TimeShift[];
  setShiftData: React.Dispatch<React.SetStateAction<TimeShift[]>>;
  isDirty: boolean;
  disabled?: boolean;
};

export const WorkingHour = ({
  shift,
  shiftData,
  setShiftData,
  isDirty,
  disabled,
}: WorkingHourProps): React.JSX.Element => {
  const [selectedDays, setSelectedDays] = useState<string[]>(shift?.days);
  // to change the day selection
  const handleDaySelect = (day: string): void => {
    setSelectedDays((prev) => {
      if (prev.includes(day)) {
        return prev.filter((item) => item !== day);
      }

      return [...prev, day];
    });
  };
  const [startTime, setStartTime] = useState<Date>(
    secToDate(shift?.startTime as number),
  );
  const [endTime, setEndTime] = useState<Date | undefined>(
    secToDate(shift?.endTime as number),
  );
  const [rate, setRate] = useState<number>(shift?.rateConversion);

  // to check if the data is mutated or not
  useEffect(() => {
    if (!isDirty) {
      setSelectedDays(shift?.days);
      setStartTime(secToDate(shift?.startTime as number));
      setEndTime(secToDate(shift?.endTime as number));
      setRate(shift?.rateConversion);
    }
  }, [isDirty]);

  const updateData = (
    id: string,
    updatedData: {
      days: string[];
      startTime: number;
      endTime: number;
      rateConversion: number;
    },
  ): void => {
    const index = shiftData?.findIndex((data: TimeShift) => data.id === id);
    setShiftData((prev) => {
      const dataToUpdate = [...prev];
      dataToUpdate[index] = { ...dataToUpdate[index], ...updatedData };

      return dataToUpdate;
    });
  };

  // to update the values based on the changes
  useEffect(() => {
    updateData(shift?.id ?? "", {
      days: selectedDays,
      startTime: dateToSec(startTime),
      endTime: dateToSec(endTime),
      rateConversion: rate,
    });
  }, [selectedDays, startTime, endTime, rate]);

  return (
    <div className='flex items-center justify-between p-6 border rounded-md shadow-sm'>
      <div className='space-y-2'>
        <h2 className='font-medium text-md'>{shift?.title}</h2>
        <div className='flex items-center space-x-4'>
          <p className='font-medium'>Days</p>
          <div className='flex space-x-2'>
            {WeekDays?.map((day) => {
              return (
                <Button
                  type='button'
                  variant={
                    selectedDays.includes(day?.value) ? "default" : "ghost"
                  }
                  key={day?.name}
                  onClick={() => {
                    if (disabled) {
                      return;
                    }
                    handleDaySelect(day?.value);
                    // field.onChange(selectedDays)
                  }}
                >
                  {day?.name}
                </Button>
              );
            })}
          </div>
        </div>
      </div>

      <div className='flex space-x-2'>
        <div className='space-y-2'>
          <Label className='pl-1 font-normal' htmlFor='startTime' required>
            Start Time
          </Label>

          <TimePicker12
            disabled={disabled}
            date={startTime as Date}
            setDate={(date) => {
              // setting the format of time to HH:MM
              // field.onChange(`${date?.getHours()}${date?.getMinutes()}`)
              setStartTime(date as Date);
            }}
            // limit={endTime}
            AMPM={Number(shift?.startTime) < 43200 ? "AM" : "PM"}
          />
        </div>
        <div className='space-y-2'>
          <Label className='pl-1 font-normal' htmlFor='endTime' required>
            End Time
          </Label>

          <TimePicker12
            disabled={disabled}
            date={endTime}
            setDate={(date) => {
              // setting the format of time to HH:MM
              // field.onChange(`${date?.getHours()}${date?.getMinutes()}`)
              setEndTime(date);
            }}
            AMPM={Number(shift?.startTime) < 43200 ? "AM" : "PM"}
          />
        </div>
      </div>

      <div className='space-y-2'>
        <Label className='pl-1 font-normal' htmlFor='email' required>
          Rate Conversion
        </Label>

        <Input
          type='number'
          disabled={disabled}
          defaultValue={shift?.rateConversion}
          value={rate.toString()}
          placeholder={`${rate} Times`}
          onChange={(e) => setRate(Number(e.target.value))}
        />
      </div>
      <div />
    </div>
  );
};
